// Generated by GitHub Copilot
/**
 * useFieldAutoSave Hook - Debounced auto-save for problem field changes
 *
 * This hook provides debounced auto-save functionality that calls the PATCH endpoint
 * when field values change. It handles error recovery, retry logic, and provides
 * save status feedback to the UI.
 *
 * Part of Feature F262310 - Patches Auto-Save
 * Story: US1799773
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";

/**
 * Save status states
 * @typedef {'idle' | 'saving' | 'saved' | 'error' | 'conflict'} SaveStatus
 */

/**
 * US2077763: HTTP status returned by the field-PATCH endpoint when the server's
 * bounded compare-and-swap retry is exhausted (someone else's write kept winning
 * the race). A 409 must NOT be blind-retried with the same stale value — the
 * client reconciles (re-fetch) instead.
 */
const HTTP_CONFLICT = 409;

/**
 * VersionConflictError tags a rejected save as a 409 version conflict so
 * executeSave can distinguish it from an ordinary (retryable) failure. Carries
 * the machine-readable error code/message from the API-standard body when present.
 */
export class VersionConflictError extends Error {
  constructor(message) {
    super(message);
    this.name = "VersionConflictError";
    this.isVersionConflict = true;
    this.status = HTTP_CONFLICT;
  }
}

/**
 * US2097708: the two retryable 4xx statuses get throttle-aware retry timing. 429 is the server
 * asking us to slow down; 408 means the request timed out in transit. Retrying either on a flat
 * delay makes the condition worse. 5xx and network failures are deliberately absent -- they stay
 * on the flat retryDelayMs.
 *
 * This is also the carve-out set isNonRetryableClientError reads, deliberately: "retryable
 * despite being a 4xx" and "needs throttle-aware retry timing" are the same two statuses, and
 * keeping ONE Set means the classifier and the delay resolver cannot drift apart.
 */
const THROTTLE_AWARE_STATUSES = new Set([408, 429]);

/**
 * Ceiling for a throttle-aware retry delay. Retry-After is attacker- or
 * misconfiguration-controlled, so it must be clamped: `Retry-After: 86400` would otherwise pin the
 * autosave indicator on "saving" for a day. Deliberately high -- clamping below what the server
 * asked reintroduces the hammering this fixes.
 */
const THROTTLE_MAX_DELAY_MS = 30000;

/**
 * How long the transient 'conflict' ("Reloaded") activity status shows before settling back to
 * idle (US2077763). Longer than the success path's 1s hold because a dropped edit is a more
 * important heads-up, but deliberately NOT sticky: after the reconcile re-fetch the view shows the
 * server's winning value and nothing is wrong, so the indicator must return to a resting state.
 * The durable "re-apply your change" message lives in the dismissible conflict notice, not here.
 */
const CONFLICT_INDICATOR_MS = 4000;

/**
 * Parse an RFC 9110 Retry-After value into milliseconds. Handles both permitted forms
 * (delta-seconds and HTTP-date) and returns null for anything malformed so a junk header degrades
 * to normal backoff instead of producing NaN.
 *
 * @param {string|null|undefined} headerValue - Raw Retry-After header value
 * @returns {number|null} Delay in milliseconds (may be negative for a past date), or null
 */
export const parseRetryAfterMs = (headerValue) => {
  if (typeof headerValue !== "string") {
    return null;
  }

  const raw = headerValue.trim();
  if (!raw) {
    return null;
  }

  // delta-seconds form. Anything else ("5s", signed, decimal) falls through to the date branch.
  if (/^\d+$/.test(raw)) {
    const seconds = Number(raw);
    return Number.isFinite(seconds) ? seconds * 1000 : null;
  }

  // HTTP-date form, e.g. "Wed, 21 Oct 2026 07:28:00 GMT".
  const targetMs = Date.parse(raw);
  if (Number.isNaN(targetMs)) {
    return null;
  }

  return targetMs - Date.now();
};

/**
 * Read and parse Retry-After off a response without assuming a real Headers object. The optional
 * calls are load-bearing: fetch mocks routinely omit `headers`, and a bare
 * `response.headers.get(...)` would turn a throttled save into a hard, unretryable TypeError.
 *
 * @param {Object} response - Fetch response (or test double)
 * @returns {number|null} Parsed delay in milliseconds, or null when absent/malformed
 */
const readRetryAfterMs = (response) => {
  try {
    return parseRetryAfterMs(response?.headers?.get?.("Retry-After"));
  } catch {
    return null;
  }
};

/**
 * Resolve the delay before a throttle-aware retry. Honours the server's Retry-After when usable,
 * else exponential backoff in the same shape as retryApiCall in connections/featureFlags.js
 * (baseDelay * 2^attempt). Clamped into [baseDelayMs, THROTTLE_MAX_DELAY_MS]; the floor stops a
 * zero or past-dated Retry-After becoming an immediate resend.
 *
 * @param {number|null} retryAfterMs - Parsed Retry-After delay, or null
 * @param {number} attempt - Zero-based retry attempt (0 for the first retry)
 * @param {number} baseDelayMs - Configured retryDelayMs
 * @returns {number} Delay in milliseconds
 */
const resolveThrottleDelayMs = (retryAfterMs, attempt, baseDelayMs) => {
  const requested =
    typeof retryAfterMs === "number" && Number.isFinite(retryAfterMs)
      ? retryAfterMs
      : baseDelayMs * Math.pow(2, attempt);

  // Math.max on the ceiling keeps the clamp coherent if a caller configures a retryDelayMs larger
  // than the ceiling; without it the floor would exceed the ceiling and Math.min would win.
  const ceiling = Math.max(baseDelayMs, THROTTLE_MAX_DELAY_MS);
  return Math.min(Math.max(requested, baseDelayMs), ceiling);
};

/**
 * Read a field path's current retry count without creating an entry for it. Absence is
 * deliberately equivalent to zero -- see clearRetryState for why that bounds the Map.
 *
 * @param {Map<string, {count: number, timerId: any}>} retryStates - Per-field retry bookkeeping
 * @param {string} fieldPath - The field path to read
 * @returns {number} Retries already attempted for this field path
 */
const readRetryCount = (retryStates, fieldPath) => {
  const entry = retryStates.get(fieldPath);
  return entry ? entry.count : 0;
};

/**
 * Drop a field path's retry bookkeeping, cancelling any timer the entry still holds. Called on
 * both terminal outcomes -- success and give-up -- which is what keeps the Map bounded by the
 * fields currently mid-retry rather than by every field ever edited. Cancelling the held timer
 * matters on the success path: a still-pending retry for this field captured an OLDER value for
 * the SAME field path, and letting it fire would PATCH that stale value over the one that just
 * saved. Newest write wins.
 *
 * @param {Map<string, {count: number, timerId: any}>} retryStates - Per-field retry bookkeeping
 * @param {string} fieldPath - The field path to clear
 * @returns {void}
 */
const clearRetryState = (retryStates, fieldPath) => {
  const entry = retryStates.get(fieldPath);
  if (entry && entry.timerId) {
    clearTimeout(entry.timerId);
  }
  retryStates.delete(fieldPath);
};

/**
 * Cancel and drop a field path's pending debounce, returning whatever entry it held. The returned
 * entry is what lets save() carry the superseded call's oldValue forward when a field is edited
 * twice inside one debounce window.
 *
 * @param {Map<string, {timerId: any, oldValue: any}>} debounceStates - Per-field debounce bookkeeping
 * @param {string} fieldPath - The field path to cancel
 * @returns {{timerId: any, oldValue: any}|null} The cancelled entry, or null if there was none
 */
const takePendingDebounce = (debounceStates, fieldPath) => {
  const pending = debounceStates.get(fieldPath);
  if (pending && pending.timerId) {
    clearTimeout(pending.timerId);
  }
  debounceStates.delete(fieldPath);
  return pending || null;
};

/**
 * Record a field path's unresolved failure, returning a NEW Map. The delete-then-set moves the
 * field to the end of the insertion order, which is what makes "the last entry" mean "the most
 * recent failure" for the derived single `error`.
 *
 * @param {Map<string, Error>} current - Current unresolved-failure map
 * @param {string} fieldPath - The field path that failed
 * @param {Error} err - The failure to record
 * @returns {Map<string, Error>} A new Map with this field path's failure recorded last
 */
const withFieldError = (current, fieldPath, err) => {
  const next = new Map(current);
  next.delete(fieldPath);
  next.set(fieldPath, err);
  return next;
};

/**
 * Clear ONE field path's unresolved failure, leaving every other field's failure standing.
 *
 * US2097708: returning the SAME Map reference when there is nothing to clear is load-bearing, not
 * an optimisation. save() runs on every edit to any of the ~20 field paths funnelling through one
 * hook instance; a fresh Map every time would re-render the whole ProblemForm tree per keystroke
 * and hand consumers a new `errors` array identity each render.
 *
 * @param {Map<string, Error>} current - Current unresolved-failure map
 * @param {string} fieldPath - The field path to clear
 * @returns {Map<string, Error>} A new Map without this field path, or `current` unchanged
 */
const withoutFieldError = (current, fieldPath) => {
  if (!current.has(fieldPath)) {
    return current;
  }
  const next = new Map(current);
  next.delete(fieldPath);
  return next;
};

/**
 * US2092239 / US2097708: extract the machine-readable error code from a PATCH error body.
 *
 * TWO envelope shapes are live in this codebase and this must handle BOTH, which is exactly why
 * the nested lookup on its own is not enough:
 *   - NESTED, per docs/standards/api-standards.md §7: {"error":{"code","message"}} -- what
 *     `writeAPIError` (api_error.go, US2092239) emits, e.g. MISSING_CONFIRMED_PEAK_PRIORITY.
 *   - FLAT: {"error":"<code>","message":"..."} -- what the US2097708 rejection handlers emit:
 *     handler_promoted_record_conflict.go's 409 `promoted_record_write_refused`,
 *     `writePromotedRecordPatchRejection`, and the 400 `temporary_identity_on_create` /
 *     `temporary_rename_target` arms. Running ONLY the nested lookup against one of these returns
 *     null, which silently costs the operator the specific rejection copy and leaves
 *     ProblemForm's code-keyed messaging with nothing to branch on.
 *
 * Nested is checked FIRST so an api-standard body is never mis-read. The flat branch is guarded on
 * `typeof === "string"`, which is load-bearing: a nested body whose `error` object carries no
 * `code` must yield null, never a stringified object.
 *
 * Returns null for a plain-text body, an unparseable body, or a body with no code, so callers can
 * fall back to a generic message. Never throws.
 *
 * @param {string} errorText - Raw response body.
 * @returns {string|null} The error code, or null when absent.
 */
export const parseAPIErrorCode = (errorText) => {
  if (!errorText) {
    return null;
  }

  let parsed;
  try {
    parsed = JSON.parse(errorText);
  } catch {
    return null;
  }

  const nestedCode = parsed?.error?.code;
  if (typeof nestedCode === "string" && nestedCode) {
    return nestedCode;
  }

  const flatCode = parsed?.error;
  return typeof flatCode === "string" && flatCode ? flatCode : null;
};

/**
 * US2092239 / US2097708: the SINGLE retry classifier for a failed PATCH.
 *
 * A 4xx is normally a client error -- the request itself is wrong (a refused resolve, a validation
 * failure, an expired session, the 409 `promoted_record_write_refused`, the 400
 * `temporary_rename_target`). Replaying the identical request cannot change the answer, so
 * retrying only delays the operator's feedback by maxRetries * retryDelayMs, multiplies the
 * failure telemetry, and fires a burst of rejected PATCHes at a MIM who is already blocked. 5xx
 * and network failures stay retryable.
 *
 * 408 and 429 are carved OUT and remain RETRYABLE. Neither is a verdict on the merits: 408 means
 * the request timed out in transit, 429 is the server asking us to slow down -- so the identical
 * body resent later can succeed. Classified permanent they skipped every retry, landed straight on
 * status "error", and dropped the operator's autosaved edit outright behind a raw
 * "PATCH failed (429)". The carve-out set is THROTTLE_AWARE_STATUSES, shared with the delay
 * resolver so the two cannot drift. Same carve-out `retryApiCall` makes in
 * connections/featureFlags.js. Do NOT widen this to "5xx only" -- 401/403/404/409/422 genuinely
 * must not be retried.
 *
 * This predicate is the ONLY expression of the rule. `callPatchEndpoint` stamps its answer onto
 * `patchError.retryable` and `executeSave` reads that flag, so there is no second copy to drift.
 *
 * @param {Error} err - Error thrown by (or being built in) callPatchEndpoint.
 * @returns {boolean} True when the error must not be retried.
 */
export const isNonRetryableClientError = (err) =>
  typeof err?.status === "number" &&
  err.status >= 400 &&
  err.status < 500 &&
  !THROTTLE_AWARE_STATUSES.has(err.status);

/**
 * Hook for debounced field auto-save with PATCH endpoint
 *
 * @param {string} problemId - The problem ID (e.g., "PRB123456")
 * @param {Object} options - Configuration options
 * @param {number} [options.debounceMs=1000] - Debounce delay in milliseconds
 * @param {number} [options.maxRetries=3] - Maximum number of retry attempts on failure
 * @param {number} [options.retryDelayMs=1000] - Delay between retry attempts
 * @param {Function} [options.onSuccess] - Callback on successful save
 * @param {Function} [options.onError] - Callback on error, invoked as
 *   onError(err, fieldPath). US2092239: fieldPath was added (mirroring onSaved)
 *   so a consumer can roll back the specific field it optimistically applied —
 *   without it a failed resolve is indistinguishable from any other failed save.
 *   `err.status` carries the HTTP status and `err.code` the api-standard error
 *   code when the server sent one.
 * @param {Function} [options.onSaved] - Callback on successful save with fieldPath and timestamp parameters
 * @param {Function} [options.onConflict] - US2077763: callback invoked when the save is rejected
 *   with HTTP 409 (version conflict). The consumer should re-fetch the current problem so the view
 *   refreshes to the winning server state. Called with the VersionConflictError.
 * @param {string} [options.apiBase='/api/v1'] - Base API URL
 *
 * @returns {Object} - { save, saveNow, status, error, errors, lastSaved }
 * @returns {Function} returns.save - Function to trigger save: save(fieldPath, newValue, oldValue)
 * @returns {Function} returns.saveNow - Same signature as save, but bypasses the
 *   debounce and issues the PATCH immediately. US2092239: use this for deliberate
 *   one-time commits (e.g. confirming the peak-priority modal), which are not the
 *   per-keystroke traffic the debounce exists to collapse. Does not disturb any
 *   save already pending in the debounce -- this field's or any other's.
 * @returns {SaveStatus} returns.status - Current save status. Reads 'error' whenever ANY field has
 *   an unresolved failure, regardless of what another field did afterwards
 * @returns {Error|null} returns.error - Most recent unresolved failure (carries `fieldPath`), or
 *   null when no field has an unresolved failure
 * @returns {Array<Error>} returns.errors - EVERY unresolved failure, oldest first, each carrying
 *   its own `fieldPath`. Empty when nothing is failing
 * @returns {Date|null} returns.lastSaved - Timestamp of last successful save
 *
 * @example
 * const { save, status, error } = useFieldAutoSave('PRB123456', {
 *   debounceMs: 1000,
 *   onSuccess: () => console.log('Saved!'),
 *   onError: (err) => console.error('Save failed:', err)
 * });
 *
 * // Trigger save when field changes
 * const handleTitleChange = (newTitle) => {
 *   setTitle(newTitle);
 *   save('title', newTitle, originalTitle);
 * };
 */
export const useFieldAutoSave = (problemId, options = {}) => {
  const {
    debounceMs = 1000,
    maxRetries = 3,
    retryDelayMs = 1000,
    onSuccess = null,
    onError = null,
    onSaved = null,
    onConflict = null,
    apiBase = "/api/v1",
  } = options;

  // State
  //
  // US2097708: `activityStatus` tracks only what the CURRENT save activity is doing
  // (idle/saving/saved). It is deliberately NOT the value returned as `status` -- see the derived
  // `status` below. A single flat status setter let a sibling field's success overwrite a failed
  // field's "error" with "saved".
  const [activityStatus, setActivityStatus] = useState("idle");

  // US2097708: unresolved failures keyed by fieldPath, replacing a single `error` slot. One hook
  // instance serves ~20 field paths through one autoSave(...), and a single slot meant a SIBLING
  // field's success ran setError(null) and erased a permanent rejection the operator had not seen
  // -- e.g. priority refused with a 409 promoted_record_write_refused (retryable=false), then
  // message_for_clients returning 200 100ms later flipped the indicator to a green "Saved" with no
  // trace the priority edit was ever refused. That reproduces the exact silent-loss outcome the
  // per-field debounce fix was meant to end, just by a different mechanism, and it defeats D8
  // ("the rejection is shown to the operator inline"). An entry is created only by a TERMINAL
  // failure and removed only by ITS OWN field's next attempt, success, or edit.
  const [fieldErrors, setFieldErrors] = useState(() => new Map());

  const [lastSaved, setLastSaved] = useState(null);

  // Refs
  const mountedRef = useRef(true);

  // US2097708: pending-debounce bookkeeping keyed by fieldPath -- one { timerId, oldValue } entry
  // per field. A single timer handle plus a single pending-save slot silently DROPPED edits:
  // save() overwrote the one slot regardless of field path, so two DIFFERENT fields edited inside
  // one debounce window produced exactly ONE PATCH carrying only the second field's body -- no
  // error, no status change, no trace. One hook instance serves ~20 field paths through a single
  // autoSave(...). Same-field coalescing is unchanged: a second save() for the SAME key still
  // supersedes its own timer, so rapid edits to one field collapse into one PATCH.
  const debounceStateRef = useRef(new Map());

  // US2097708: retry bookkeeping keyed by fieldPath -- one { count, timerId } entry per field. A
  // single shared counter and timer handle broke as soon as two chains could overlap: save() for
  // field B restarts the debounce while executeSave for field A is still awaiting its PATCH, and
  // then B's timer handle overwrote A's (leaving only B's cancellable) while B's backoff read a
  // counter A had been incrementing. The window is wide because a throttle-aware delay clamps at
  // THROTTLE_MAX_DELAY_MS (30s) against a flat retryDelayMs default of 1s.
  const retryStateRef = useRef(new Map());

  // US2097708: the saved -> idle transition timer. ONE handle for the hook, deliberately NOT
  // keyed by field path -- see the scheduling site in executeSave for the reasoning.
  const idleTimerRef = useRef(null);

  // Setup and cleanup on mount/unmount
  useEffect(() => {
    mountedRef.current = true;
    // Captured here rather than read in the cleanup below to satisfy
    // react-hooks/exhaustive-deps. Safe and equivalent: each Map instance is created once by
    // useRef and never reassigned, so these are the same objects the cleanup needs to drain.
    const retryStates = retryStateRef.current;
    const debounceStates = debounceStateRef.current;

    return () => {
      mountedRef.current = false;
      // US2097708: EVERY pending debounce, not just the most recently scheduled one -- with
      // debounces scoped per field path several can legitimately be in flight at once, and an
      // uncancelled one keeps its closure alive past teardown. The mountedRef guard inside each
      // callback is the second line of defence, not a substitute for cancelling here.
      debounceStates.forEach((entry) => {
        if (entry.timerId) {
          clearTimeout(entry.timerId);
        }
      });
      debounceStates.clear();
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      // US2097708: EVERY pending retry, for the same reason as the debounce timers above.
      retryStates.forEach((entry) => {
        if (entry.timerId) {
          clearTimeout(entry.timerId);
        }
      });
      retryStates.clear();
    };
  }, []);

  /**
   * Call the PATCH endpoint to save field change
   * @param {string} fieldPath - The field path to save
   * @param {any} newValue - New field value
   * @param {any} oldValue - Previous field value (for conflict detection)
   * @returns {Promise<Object>} - Response data
   */
  const callPatchEndpoint = useCallback(
    async (fieldPath, newValue, oldValue, extraFields) => {
      const url = `${apiBase}/problem/${problemId}/field`;

      const requestBody = {
        fieldPath,
        value: newValue,
      };

      // Add oldValue if provided (for conflict detection)
      if (oldValue !== undefined && oldValue !== null) {
        requestBody.oldValue = oldValue;
      }

      // US2028896: Merge any extra top-level body fields (e.g.
      // confirmedPeakPriority on the resolve/incident_end PATCH). Callers that
      // pass nothing get the original body shape unchanged.
      if (extraFields && typeof extraFields === "object") {
        Object.assign(requestBody, extraFields);
      }

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies for authentication
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[useFieldAutoSave] PATCH failed:", {
          status: response.status,
          errorText,
        });
        // US2077763: a 409 with code `version_conflict` means the server's CAS retry
        // was exhausted — a concurrent write won. Tag it so executeSave reconciles
        // (re-fetch) instead of blind-retrying the SAME stale value, which would just
        // lose the race again. This MUST run before the generic patchError construction
        // below so the conflict keeps its VersionConflictError type.
        //
        // Discriminate BY CODE, not by status alone: US2097708's promoted-record
        // rejection (`promoted_record_write_refused`) is ALSO a 409 but must NOT
        // reconcile — it falls through to the rich patchError path below, where
        // isNonRetryableClientError marks it retryable=false and it surfaces as a sticky
        // per-field error. A CAS 409 is the DEFAULT meaning of a 409 here: it carries
        // code `version_conflict` (writeVersionConflict) or no machine code at all
        // (empty/plain body). So reconcile when the code is `version_conflict` OR absent,
        // and only skip reconciliation for a 409 that names a DIFFERENT explicit code.
        if (response.status === HTTP_CONFLICT) {
          const conflictCode = parseAPIErrorCode(errorText);
          if (conflictCode === "version_conflict" || conflictCode === null) {
            throw new VersionConflictError(
              `PATCH rejected (409 version conflict): ${
                errorText || response.statusText
              }`
            );
          }
        }
        // US2097708: carry the HTTP status on the thrown error so executeSave can tell a
        // permanent rejection from a transient failure, and surface the server's own wording --
        // the flat {error, message} envelope would otherwise reach err.message as a JSON blob.
        //
        // US2092239 established that `status` and `code` belong on the error OBJECT rather than
        // only inside the message prose: consumers were regexing the status back out of the
        // string, which was rejected at review. `status` matches the shape PR #631's
        // VersionConflictError uses (`.status = 409`) so both can be branched on with one rule.
        let message = `PATCH failed (${response.status}): ${
          errorText || response.statusText
        }`;
        try {
          const parsed = JSON.parse(errorText);
          if (parsed && typeof parsed.message === "string" && parsed.message) {
            message = parsed.message;
          }
        } catch {
          // Body is not JSON — keep the original text-based message.
        }

        const patchError = new Error(message);
        patchError.status = response.status;
        // Handles BOTH error envelopes on this codebase -- the nested api-standard one and the
        // flat one the US2097708 rejection handlers emit. See parseAPIErrorCode. A handler that
        // still returns plain text yields null, which consumers must tolerate.
        patchError.code = parseAPIErrorCode(errorText);
        // US2097708: which field was refused. Without it a consumer receiving this error through
        // `onError` or the returned `error` could not attribute the failure to anything -- the
        // success path already reports `onSaved(fieldPath, ...)`, so the failure path was the only
        // one that told the operator "something failed" without saying what.
        patchError.fieldPath = fieldPath;
        // Retryability comes from the one named classifier, never from a second inline copy of the
        // range test -- read isNonRetryableClientError for the rule and for why 408/429 are
        // carved out of it. Must run AFTER `status` is assigned, which is what it reads.
        patchError.retryable = !isNonRetryableClientError(patchError);
        // US2097708: only the throttle-aware statuses carry Retry-After forward -- 5xx retry
        // cadence is pre-existing behaviour and deliberately stays on the flat retryDelayMs.
        patchError.retryAfterMs = THROTTLE_AWARE_STATUSES.has(response.status)
          ? readRetryAfterMs(response)
          : null;
        throw patchError;
      }

      const result = await response.json();
      return result;
    },
    [apiBase, problemId]
  );

  /**
   * Execute save with retry logic
   * @param {string} fieldPath - The field path to save
   * @param {any} newValue - New field value
   * @param {any} oldValue - Previous field value
   */
  const executeSave = useCallback(
    async (fieldPath, newValue, oldValue, extraFields) => {
      if (!mountedRef.current) {
        return;
      }

      try {
        setActivityStatus("saving");
        // Clear ONLY this field path's unresolved failure: this attempt is that field's own retry
        // of it. Another field's failure is a different key and stays standing.
        setFieldErrors((current) => withoutFieldError(current, fieldPath));

        const result = await callPatchEndpoint(
          fieldPath,
          newValue,
          oldValue,
          extraFields
        );

        if (!mountedRef.current) return;

        // Success
        setActivityStatus("saved");
        // US2097708: resolve ONLY this field path. A blanket clear here is what let
        // message_for_clients's 200 erase priority's unacknowledged 409. This field succeeding
        // says nothing about whether another field's rejection has been dealt with.
        setFieldErrors((current) => withoutFieldError(current, fieldPath));
        setLastSaved(new Date());
        // Reset ONLY this field's retry counter; pruning the entry IS that reset.
        clearRetryState(retryStateRef.current, fieldPath);

        if (onSuccess) {
          onSuccess(result);
        }

        if (onSaved) {
          onSaved(fieldPath, result.timestamp);
        }

        // Auto-transition back to idle after 1 second, held in a ref so unmount can cancel it.
        //
        // US2097708: a SINGLE handle is correct here, unlike the per-field retry timers and the
        // per-field error map, because `activityStatus` (like `lastSaved`) is one piece of state
        // rather than one per field -- last settler wins across fields, so only the most recently
        // settled save can own what the activity indicator shows. Keying this by field path would
        // let field A's older transition fire mid-way through field B's "saved" window and blank
        // it early. Failures deliberately do NOT work this way: an unacknowledged rejection is
        // not something a later save is entitled to overwrite, so those are keyed per field.
        //
        // The mountedRef guard inside STAYS -- see the retry timer below for why.
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current);
        }
        idleTimerRef.current = setTimeout(() => {
          if (mountedRef.current) {
            setActivityStatus("idle");
          }
        }, 1000);
      } catch (err) {
        console.error("[useFieldAutoSave] Save error:", err);
        if (!mountedRef.current) return;

        // US2097708: callPatchEndpoint stamps fieldPath on the errors IT builds, but a network
        // failure or an offline fetch never reaches that code and throws a TypeError the browser
        // made. Stamp those here so every error a consumer can observe is attributable. Guarded
        // rather than unconditional: `throw "string"` is legal, and the fieldPath already set by
        // callPatchEndpoint is the authoritative one.
        if (err && typeof err === "object" && err.fieldPath === undefined) {
          err.fieldPath = fieldPath;
        }

        // US2077763: a 409 version conflict must NOT be blind-retried with the
        // same stale value — that would just re-lose the race and could loop up
        // to maxRetries times sending known-stale data. Break out of the retry
        // path entirely: reset THIS field's retry bookkeeping, surface a distinct
        // 'conflict' status, record the failure under this field path, and trigger
        // reconciliation (re-fetch the current problem) via onConflict so the view
        // refreshes to the winning server state. There is no timer scheduled here,
        // so this path cannot loop. Deliberately ABOVE the retryable/throttle
        // decision below: a 409 is a non-retryable 4xx, so that logic would
        // otherwise swallow it and the re-fetch would never run.
        if (err && err.isVersionConflict) {
          console.warn(
            "[useFieldAutoSave] Version conflict (409) — reconciling instead of retrying"
          );
          // Per-field retry bookkeeping (US2097708 replaced the single retryCountRef
          // with retryStateRef); clearing this field's entry IS its retry reset and
          // cancels any pending retry timer holding an older value for this field.
          clearRetryState(retryStateRef.current, fieldPath);
          // US2077763 (PR #631): a debounced edit for THIS field queued while
          // this PATCH was in flight would otherwise fire AFTER the reconcile and re-clobber
          // the winning value with the pre-reload edit. Cancel this field's pending debounce
          // (only this field's — others stay armed) so the reconcile is the last word.
          takePendingDebounce(debounceStateRef.current, fieldPath);
          // Surface a distinct 'conflict' activity status. Deliberately NOT recorded in
          // fieldErrors: the derived `status` collapses any non-empty fieldErrors to
          // "error" (US2097708 sticky-rejection rule), which would mask "conflict". A 409
          // is reconciled via onConflict (re-fetch), not left as a sticky per-field
          // rejection the operator must clear — so it stays out of the error map.
          setActivityStatus("conflict");
          if (onConflict) {
            onConflict(err);
          } else if (onError) {
            // No reconcile handler wired — fall back to onError so the conflict
            // is still surfaced rather than silently swallowed.
            onError(err, fieldPath);
          }
          // US2077763 (PR #631): settle the transient 'conflict' status back to idle, the
          // same single-handle way the success path settles 'saved' (last settler wins across
          // fields). 'conflict' must NOT be sticky: once onConflict has re-fetched, the view
          // shows the server's winning value and nothing is wrong, so a permanently-warning
          // indicator would misrepresent a settled state (and duplicate the durable "re-apply
          // your change" message, which lives in the dismissible conflict notice). Held longer
          // than 'saved' (1s) because a dropped edit is a more important heads-up.
          if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
          }
          idleTimerRef.current = setTimeout(() => {
            if (mountedRef.current) {
              setActivityStatus("idle");
            }
          }, CONFLICT_INDICATOR_MS);
          return;
        }

        // US2097708: never retry a permanent rejection. Network failures and thrown
        // non-HTTP errors carry no `retryable` flag and keep the original behaviour.
        const isRetryable = err?.retryable !== false;

        // US2092239, carried forward: PR #631 (US2077763) adds a 409 version-conflict reconcile.
        // That branch belongs ABOVE this decision, not below it -- a 409 is a non-retryable 4xx,
        // so the give-up branch would otherwise swallow it and the re-fetch would never run.

        // US2097708: the retry count is read from THIS field path's entry. It used to come from
        // a single ref shared by every field, so an overlapping chain for another field could
        // burn through this field's retry budget and skew its backoff.
        const retryStates = retryStateRef.current;
        const retriesSoFar = readRetryCount(retryStates, fieldPath);

        if (isRetryable && retriesSoFar < maxRetries) {
          // Zero-based to match retryApiCall, whose first retry waits baseDelay * 2^0. Read
          // synchronously here rather than inside the setTimeout: executeSave recurses, and the
          // entry is mutated by the next attempt in this field's own chain.
          const attempt = retriesSoFar;
          const retryNumber = retriesSoFar + 1;
          const delayMs = THROTTLE_AWARE_STATUSES.has(err?.status)
            ? resolveThrottleDelayMs(err.retryAfterMs, attempt, retryDelayMs)
            : retryDelayMs;

          console.warn(
            `[useFieldAutoSave] Save failed, retry ${retryNumber}/${maxRetries} in ${delayMs}ms:`,
            err.message
          );

          // Retry after delay. The handle is stored per field path so the unmount cleanup can
          // cancel every one of them. The mountedRef guard inside STAYS -- a timer can be
          // superseded while still mounted (an overlapping chain for the SAME field replaces the
          // entry below), and the guard is the only thing stopping an already-queued callback
          // from re-entering executeSave after teardown.
          const existing = retryStates.get(fieldPath);
          if (existing && existing.timerId) {
            // US2097708: always a handle for THIS field path -- another field's retry lives under
            // a different key and is never touched here. It is NOT necessarily this chain's own
            // previous handle, though: save() never cancelled in-flight work, so two retry chains
            // for the SAME field path could coexist over this one entry and the handle here may
            // belong to the other chain. Either way it carries an OLDER value for this same field,
            // so cancelling it is correct under newest-write-wins. What it does NOT do is reset
            // the shared `count` -- see save(), which owns that reset.
            clearTimeout(existing.timerId);
          }

          retryStates.set(fieldPath, {
            count: retryNumber,
            timerId: setTimeout(() => {
              if (mountedRef.current) {
                executeSave(fieldPath, newValue, oldValue, extraFields);
              }
            }, delayMs),
          });
        } else {
          if (isRetryable) {
            console.error("[useFieldAutoSave] Max retries exceeded");
          } else {
            console.error(
              `[useFieldAutoSave] Save rejected (${err?.status}), not retrying:`,
              // US2092239: log the machine-readable code when the server sent one -- it is the
              // discriminator a responder greps for, and the prose is often the raw body.
              err?.code || err?.message
            );
          }
          setActivityStatus("error");
          // US2097708: recorded UNDER THIS FIELD PATH, so a sibling field settling afterwards
          // cannot displace it. It is cleared only when this field itself is retried, saved, or
          // edited again.
          setFieldErrors((current) => withFieldError(current, fieldPath, err));
          // Terminal for this field: prune its entry. No retry is scheduled here, but the
          // handle this prunes is NOT necessarily an already-fired one: save() never cancelled
          // in-flight work, so a coexisting chain for this SAME field path may have stored a
          // LIVE retry timer under this key, and this cancels it. Reachable both when this
          // chain fails non-retryably and when it reads the shared `count` at maxRetries --
          // see the retryable branch above, which concedes the same coexistence over this one
          // entry. KNOWN RESIDUAL, accepted for this story: closing it needs the per-field
          // generation token save() describes.
          clearRetryState(retryStates, fieldPath);

          if (onError) {
            onError(err, fieldPath);
          }
        }
      }
    },
    [
      callPatchEndpoint,
      maxRetries,
      retryDelayMs,
      onSuccess,
      onError,
      onSaved,
      onConflict,
    ]
  );

  /**
   * Trigger a debounced save
   * @param {string} fieldPath - The field path to save
   * @param {any} newValue - New field value to save
   * @param {any} oldValue - Previous field value (optional, for conflict detection)
   */
  const save = useCallback(
    (fieldPath, newValue, oldValue = null, extraFields = null) => {
      const debounceStates = debounceStateRef.current;

      // Only ever THIS field path's own pending debounce. Cancelling it preserves same-field
      // coalescing; another field's pending debounce is a different key and is left running.
      const superseded = takePendingDebounce(debounceStates, fieldPath);

      // oldValue on a coalesced same-field burst: the FIRST call's oldValue wins. The superseded
      // call was never sent, so the server still holds the base the first call in this window
      // described. Last-call-wins would report a "previous" value that only ever existed
      // locally: edit priority EP4 -> EP2 -> EP1 inside one window and the PATCH claims it
      // changed from EP2, which the server never stored -- and the audit/timeline trail is
      // phrased from the old value ("<Field> changed from X to Y", backend/engine/engine.go).
      //
      // Client-side only today: PatchFieldRequest has no oldValue field and writePatchRecord is
      // called with the server's own currentValue, so the body's oldValue is sent and discarded.
      const baseOldValue = superseded ? superseded.oldValue : oldValue;

      // US2097708 (F4): a fresh user edit to this field starts this field's retry budget clean.
      // Without this, the entry sits at count == maxRetries for the WHOLE final backoff before
      // give-up -- up to THROTTLE_MAX_DELAY_MS (30s) after a 429. An edit landing in that window
      // read retriesSoFar == maxRetries, failed the `retriesSoFar < maxRetries` check in
      // executeSave, and went straight to a hard error having made ZERO retry attempts, because
      // save() never touched retryStateRef and there is no in-flight registry to scope the count
      // to one chain.
      //
      // save() is the right boundary: it is the only place a NEW intended value enters, which is
      // exactly what deserves a full budget. Scoped to this one key, so a legitimate in-flight
      // retry for a DIFFERENT field is untouched -- clearRetryState only reads and deletes
      // retryStates.get(fieldPath), and other fields live under other keys. Cancelling THIS
      // field's pending retry is intended, not collateral: that timer holds a value this call
      // supersedes, the same newest-write-wins rule clearRetryState applies on the success path.
      //
      // KNOWN RESIDUAL, and this clear WIDENED it: it also zeroes the counter for a chain that is
      // already AWAITING its PATCH for this same field, which the clear cannot reach -- only the
      // pending TIMER is cancellable and a chain mid-await holds none. Should that chain then fail
      // BEFORE the newer save settles, it reads 0 instead of its own progress and gets a full
      // budget of fresh attempts at the SUPERSEDED value, where before it would have resumed
      // nearer exhaustion. Only that pre-settle window is new: the success and give-up clears
      // already zeroed the counter once the newer save settled. It is also self-limiting, because
      // both chains share this one per-field entry -- the newer save settling cancels whatever
      // timer the stale chain last stored here (clearRetryState on success, the clearTimeout of
      // `existing.timerId` on failure). Closing it properly needs a per-field generation token so
      // a stale chain can tell it has been superseded; that changes retry behaviour and is out of
      // scope for this story.
      clearRetryState(retryStateRef.current, fieldPath);

      // Set status to idle while debouncing (not yet saving)
      setActivityStatus("idle");
      // US2097708: clear ONLY this field path's unresolved failure. Typing in a DIFFERENT field
      // used to run a blanket setError(null) and wipe a rejection the operator had not seen --
      // the same silent loss a sibling field's success caused. Editing the failed field IS the
      // operator acting on its error, so clearing its own entry here is correct.
      setFieldErrors((current) => withoutFieldError(current, fieldPath));

      // Start this field path's own debounce timer. newValue and extraFields come from this
      // closure rather than the Map, so the surviving timer is inherently the latest call's.
      const timerId = setTimeout(() => {
        // A debounce entry is settled the instant its timer fires, so prune it here -- the one
        // place a debounce becomes terminal. That bounds the Map by the fields currently
        // mid-debounce rather than by every field ever edited. No save() can interleave between
        // this timer firing and this line, so the entry under this key is always this timer's.
        debounceStates.delete(fieldPath);

        if (!mountedRef.current) {
          return;
        }

        executeSave(fieldPath, newValue, baseOldValue, extraFields);
      }, debounceMs);

      debounceStates.set(fieldPath, { timerId, oldValue: baseOldValue });
    },
    [debounceMs, executeSave]
  );

  /**
   * US2092239: trigger an IMMEDIATE save, bypassing the debounce entirely.
   *
   * Debouncing exists so that typing does not fire a request per keystroke. Confirming a modal is
   * a deliberate one-time commit rather than per-keystroke traffic, so it goes straight to
   * `executeSave` instead of waiting out a debounce window.
   *
   * US2097708 correction to the original rationale: this was introduced because `save` shared ONE
   * `debounceTimerRef` and ONE `pendingSaveRef` across every field, so queueing the status PATCH
   * from the peak modal and then setting Impact End within `debounceMs` silently destroyed the
   * first write. THAT DEFECT IS FIXED -- the debounce is keyed by fieldPath (`debounceStateRef`)
   * and two different fields edited inside one window each get their own PATCH. Neither of those
   * refs exists any more; do not reason from them. `saveNow` is kept because the reason above
   * stands on its own.
   *
   * Deliberately does NOT touch `debounceStateRef`: this field's own pending debounce, and every
   * other field's, are left running. Both orderings (resolve-then-end and end-then-resolve)
   * therefore complete.
   *
   * @param {string} fieldPath - The field path to save
   * @param {any} newValue - New field value to save
   * @param {any} oldValue - Previous field value (optional, for conflict detection)
   * @param {Object} [extraFields] - Extra top-level body fields
   * @returns {Promise<void>} Resolves once the save (and any retries) settle.
   */
  const saveNow = useCallback(
    (fieldPath, newValue, oldValue = null, extraFields = null) =>
      executeSave(fieldPath, newValue, oldValue, extraFields),
    [executeSave]
  );

  // US2097708: every unresolved failure, oldest first. Each entry carries its own `fieldPath`, so
  // a consumer can attribute and render all of them rather than being handed one anonymous error.
  const errors = useMemo(() => Array.from(fieldErrors.values()), [fieldErrors]);

  // The most recent unresolved failure. withFieldError re-inserts at the end, so the last entry is
  // the newest. Kept as `error` because it is the single-error shape LeftNavigation's tooltip and
  // ProblemForm's inline alert already consume -- the change is that it is now STICKY: only the
  // failing field itself can clear it.
  const error = errors.length > 0 ? errors[errors.length - 1] : null;

  // US2097708: an unresolved failure OUTRANKS whatever the latest activity was. `status` is one
  // indicator for ~20 field paths, and a green "Saved" rendered while a permanent rejection sits
  // unacknowledged is precisely the defect -- so field B succeeding after field A was refused
  // reads "error", not "saved". This also holds "error" over a later "saving"/"idle": the
  // rejection stays visible until the field that owns it is retried, saved, or edited again.
  const status = errors.length > 0 ? "error" : activityStatus;

  return {
    save,
    saveNow,
    status,
    error,
    errors,
    lastSaved,
  };
};

export default useFieldAutoSave;
